import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Stack, Typography, chipClasses, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BEIGE_200, BEIGE_400, BLUE_600, BOX_SHADOW_LG, GRAY_50, GRAY_700, GRAY_800, GRAY_900, MOBILE_VIEW_WIDTH_QUERY, RhetorikFontFamily, WHITE } from 'constants/styling/theme'
import { carouselScrollState, carouselScrollStep } from './carouselScroll'
import { CAROUSEL_ARROW_SIZE, CarouselScrollArrow } from './CarouselScrollArrow.component'
import { ClientPrice } from '../../components/ClientPrice.component'
import type { ResolvedFeaturedEntry } from 'models/purchaseFlowRevamp'
import { MUIBadge } from 'components/common/MUIBadge'
import { ProductKindIcon } from './ProductKindIcon.component'
import { ProductPreview } from './ProductPreview.component'
import { PfClickableCard } from '../../components/PfClickableCard.component'
import { PF_CARD_HOVER_SX } from '../../components/cardHover'

interface Props {
  /** The cards to feature, in the backend's order: bundles and featured products. */
  entries: ResolvedFeaturedEntry[]
  /** Called with the clicked card; the consumer opens the product drawer on its variant. */
  onSelect: (entry: ResolvedFeaturedEntry) => void
}

/**
 * The "Featured services" carousel — pre-built
 * packs offered above the category sections.
 *
 * A pack states its **composition**, not a description: one tag per contained
 * service. The prototype carried both until 2026-09-15, when the description
 * was removed because it named in prose the same services the tags name. So a
 * bundle's `description` is deliberately not rendered here.
 *
 * Horizontal scroll rather than a grid: the section sits above the catalogue
 * and must not push it off the first screen. Cards are a fixed width with
 * scroll-snap, sized (per the prototype) so part of the next card stays
 * visible — which is what tells the reader there are more.
 *
 * A featured product (not a pack) gets the same card, with its size as the one tag and that
 * size's price, because the backend features a specific product rather than its group.
 *
 * Renders nothing when there is nothing to feature.
 */
export const FeaturedBundleSection: React.FC<Props> = ({ entries, onSelect }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  // Below the breakpoint the row is scrolled by touch, so a floating arrow
  // would only cover a card.
  const isMobile = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [{ canScrollPrev, canScrollNext }, setScrollState] = useState({ canScrollPrev: false, canScrollNext: false })

  const syncArrows = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    setScrollState(carouselScrollState(el))
  }, [])

  // The row's own width decides this, and it changes without a scroll: the
  // catalogue re-renders on search, and the window resizes.
  useEffect(() => {
    syncArrows()
    window.addEventListener('resize', syncArrows)
    return () => window.removeEventListener('resize', syncArrows)
  }, [syncArrows, entries])

  const scrollBy = useCallback((direction: 'prev' | 'next') => {
    const el = scrollerRef.current
    if (!el) return
    // Measure the pitch off two cards rather than deriving it from the card width and the gap.
    const first = el.children.item(0)
    const second = el.children.item(1)
    const pitch = first && second
      ? second.getBoundingClientRect().left - first.getBoundingClientRect().left
      : (first?.getBoundingClientRect().width ?? 0)
    const step = carouselScrollStep({ clientWidth: el.clientWidth, pitch })
    el.scrollBy({ left: direction === 'prev' ? -step : step, behavior: 'smooth' })
  }, [])

  if (entries.length === 0) return null

  return (
    <Stack gap={1.6} data-cy="pf-revamp-featured-bundles">

      <Typography
        variant="text-2xl"
        fontWeight={400}
        color={GRAY_900}
        fontFamily={RhetorikFontFamily}
      >
        {t('purchase_flow_revamp:catalog.featured_heading')}
      </Typography>

      <Box sx={{ position: 'relative' }}>

        <Stack
          ref={scrollerRef}
          onScroll={syncArrows}
          direction="row"
          gap={CARD_GAP_REM}
          sx={{
            overflowX: 'auto',
            overscrollBehaviorX: 'contain',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            // The tint on a hovered card reaches past its own box, so the row
            // needs room for it rather than clipping it at the scroll edge.
            // `scrollPaddingInline` matches, so the first card still snaps flush
            // to the column instead of stopping short by the padding.
            p: 1.2,
            m: -1.2,
            scrollPaddingInline: '1.2rem',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {entries.map((entry) => (
            <FeaturedCard key={`${entry.shell.id}|${entry.variantId}`} entry={entry} onSelect={onSelect} />
          ))}
        </Stack>

        {/* The veil over a cut-off card. A solid background block faded by a
            MASK rather than a colour-stop gradient: a gradient cannot fade a
            token to zero alpha without naming its hue, and a mask reads only
            alpha, so the colour stays the token. Driven by the same flags as the
            arrows, so veil and arrow can never disagree about whether there is
            more to see. */}
        <EdgeVeil side="left" visible={!isMobile && canScrollPrev} />
        <EdgeVeil side="right" visible={!isMobile && canScrollNext} />

        {!isMobile && canScrollPrev &&
          <CarouselScrollArrow direction="prev" onClick={scrollBy} label={t('purchase_flow_revamp:catalog.featured_scroll_left')} top="9rem" inset={`calc(${CAROUSEL_ARROW_SIZE} / -2)`} data-cy="pf-revamp-bundle-scroll-prev" />
        }

        {!isMobile && canScrollNext &&
          <CarouselScrollArrow direction="next" onClick={scrollBy} label={t('purchase_flow_revamp:catalog.featured_scroll_right')} top="9rem" inset={`calc(${CAROUSEL_ARROW_SIZE} / -2)`} data-cy="pf-revamp-bundle-scroll-next" />
        }

      </Box>

    </Stack>
  )
}

/** Width of one card. Chosen so a fourth card is half-visible at the catalogue's
 *  reading width — a cut-off card is the affordance, and the arrow is the
 *  confirmation rather than the only signal. */
const CARD_WIDTH = '25.8rem'

/** Gap between cards, in MUI spacing units (32px). */
const CARD_GAP_REM = 3.2

/** The fade over a cut-off card — see the call site for why it is a mask. */
const EdgeVeil: React.FC<{ side: 'left' | 'right'; visible: boolean }> = ({ side, visible }) => (
  <Box
    aria-hidden
    data-cy={`pf-revamp-bundle-veil-${side}`}
    sx={{
      position: 'absolute',
      zIndex: 1,
      pointerEvents: 'none',
      backgroundColor: BEIGE_200,
      opacity: visible ? 1 : 0,
      transition: 'opacity 160ms ease',
      // Overhangs the scroller's own tint allowance on every side, or the veil
      // stops short and leaves a bare strip of card at the very edge.
      top: '-1.2rem',
      bottom: '-1.2rem',
      [side]: '-1.2rem',
      width: 'calc(9.6rem + 1.2rem)',
      maskImage: `linear-gradient(to ${side}, transparent, #000 7.2rem)`,
      WebkitMaskImage: `linear-gradient(to ${side}, transparent, #000 7.2rem)`,
    }}
  />
)

const FeaturedCard: React.FC<{
  entry: ResolvedFeaturedEntry
  onSelect: (entry: ResolvedFeaturedEntry) => void
}> = ({ entry, onSelect }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  const { shell: bundle, variantId } = entry
  const variant = bundle.variants.find((v) => v.id === variantId) ?? bundle.variants[0]
  const members = variant?.bundledProducts ?? []
  const isPack = bundle.isBundle === true || members.length > 0
  // A pack states its composition; a featured product states which size it is.
  const tags = isPack
    ? members.map((member) => ({ key: member.productCode, label: member.displayName, kind: member.kind }))
    : variant ? [{ key: variant.id, label: variant.displayName, kind: variant.kind ?? '' }] : []
  const price = variant?.price

  const handleSelect = () => onSelect(entry)

  return (
    <PfClickableCard
      onActivate={handleSelect}
      gap={1.2}
      data-cy={isPack ? `pf-revamp-bundle-card-${bundle.id}` : `pf-revamp-featured-card-${variantId}`}
      sx={{
        flex: `0 0 ${CARD_WIDTH}`,
        // A flex item's basis is a FLOOR, not a width, until it is allowed to
        // shrink below its own min-content: one long unbreakable word (a German
        // compound in a document pack) pushed a single card to 323px against the
        // others' 258px. `minWidth: 0` plus breaking those words is what keeps
        // the row uniform.
        minWidth: 0,
        scrollSnapAlign: 'start',
        ...PF_CARD_HOVER_SX,
        '&:focus-visible': { backgroundColor: GRAY_50 },
      }}
    >

      {/* `alt=""` deliberately: with no artwork the tile prints its alt text as a
          caption, which put the pack's name on the card twice. The name is
          stated once, by the heading below. */}
      <ProductPreview
        preview={bundle.preview}
        alt=""
        width="100%"
        height="18rem"
        borderRadius="1rem"
        interactive
      />

      <Typography
        variant="text-lg"
        fontWeight={550}
        color={GRAY_900}
        sx={{ letterSpacing: '-0.72px', overflowWrap: 'anywhere' }}
      >
        {bundle.displayName}
      </Typography>

      {/* The composition, one tag per service. Wrapping to a second line is
          fine — it is what removing the description made room for. */}
      {tags.length > 0 &&
        <Stack direction="row" gap={0.4} flexWrap="wrap">
          {tags.map((tag) => (
            <MUIBadge
              key={tag.key}
              data-cy="pf-revamp-bundle-tag"
              label={tag.label}
              icon={<ProductKindIcon kind={tag.kind} size="1.2rem" />}
              color="gray"
              size="sm"
              sx={{ maxWidth: '100%', [`& .${chipClasses.label}`]: { whiteSpace: 'normal', overflowWrap: 'anywhere' } }}
            />
          ))}
        </Stack>
      }

      {price &&
        <Typography variant="text-sm" marginTop="auto">

          {/* A featured product is one size at one price, so only a pack says "From". */}
          {isPack &&
            <Box component="span" color={GRAY_700}>
              {t('purchase_flow_revamp:catalog.price_from')}{' '}
            </Box>
          }

          <Box component="span" display="inline-flex" color={GRAY_900} fontWeight={550}>
            <ClientPrice unitPrice={price.unitPrice} currency={price.currency} variant="text-sm" />
          </Box>

        </Typography>
      }

    </PfClickableCard>
  )
}
